<script setup>
import { ref, computed, h, nextTick, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useOperators } from 'dashboard/components-next/filter/operators';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import AutomationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditSheet from 'dashboard/components-next/polaris/cards/EditSheet.vue';
import FoldingGroup from 'dashboard/components-next/polaris/cards/FoldingGroup.vue';
import OptionSelect from 'dashboard/components-next/polaris/cards/OptionSelect.vue';
import {
  Field,
  FieldError,
  FieldLabel,
} from 'dashboard/components-next/polaris/ui/field';
import {
  generateAutomationPayload,
  getAttributes,
  getFileName,
  showActionInput,
} from 'dashboard/helper/automationHelper';
import { validateAutomation } from 'dashboard/helper/validations';
import { AUTOMATION_RULE_EVENTS, AUTOMATION_ACTION_TYPES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['create', 'edit'].includes(value),
  },
  automationTypes: {
    type: Object,
    required: true,
  },
  getConditionDropdownValues: {
    type: Function,
    required: true,
  },
  getActionDropdownValues: {
    type: Function,
    required: true,
  },
  appendNewCondition: {
    type: Function,
    required: true,
  },
  appendNewAction: {
    type: Function,
    required: true,
  },
  removeFilter: {
    type: Function,
    required: true,
  },
  removeAction: {
    type: Function,
    required: true,
  },
  resetAction: {
    type: Function,
    required: true,
  },
  onEventChange: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['save']);
const automation = defineModel('automation', { type: Object, default: null });

const INPUT_TYPE_MAP = {
  multi_select: 'multiSelect',
  search_select: 'searchSelect',
  plain_text: 'plainText',
  comma_separated_plain_text: 'plainText',
  date: 'date',
};

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const { operators } = useOperators();

// Phase 9.3 (D-G6, R2-9-5): the rule is edited in the kit's EditSheet, not a dialog; same fields, validation and
// payload. Sections fold (Q1); closing with changes asks "Discard changes?".
const sheetRef = ref(null);
const isOpen = ref(false);
const snapshot = ref('');
const conditionsRef = useTemplateRef('conditionsRef');
const errors = ref({});

const isEditMode = computed(() => props.mode === 'edit');

const titleKey = computed(() =>
  isEditMode.value ? 'AUTOMATION.EDIT.TITLE' : 'AUTOMATION.ADD.TITLE'
);
const submitKey = computed(() =>
  isEditMode.value ? 'AUTOMATION.EDIT.SUBMIT' : 'AUTOMATION.ADD.SUBMIT'
);

const getTranslatedAttributes = (type, event) => {
  return getAttributes(type, event).map(attribute => {
    const skipTranslation =
      attribute.customAttributeType ||
      ['contact_custom_attribute', 'conversation_custom_attribute'].includes(
        attribute.key
      );
    return {
      ...attribute,
      name: skipTranslation
        ? attribute.name
        : t(`AUTOMATION.ATTRIBUTES.${attribute.name}`),
    };
  });
};

const eventName = computed(() => automation.value?.event_name);

const filterTypes = computed(() => {
  const event = eventName.value;
  if (!event || !props.automationTypes[event]) return [];

  const attributes = getTranslatedAttributes(props.automationTypes, event);

  return attributes.map(attr => {
    if (attr.disabled) {
      return { value: attr.key, label: attr.name, disabled: true };
    }

    const mappedInputType = INPUT_TYPE_MAP[attr.inputType] || 'plainText';
    const options = props.getConditionDropdownValues(attr.key) || [];

    const filterOperators = (attr.filterOperators || []).map(op => {
      const enriched = operators.value[op.value];
      if (enriched) return enriched;
      return {
        value: op.value,
        label: t(`FILTER.OPERATOR_LABELS.${op.value}`),
        hasInput: true,
        inputOverride: null,
        icon: h('span', { class: 'i-ph-equals-bold !text-n-blue-11' }),
      };
    });

    return {
      attributeKey: attr.key,
      value: attr.key,
      attributeName: attr.name,
      label: attr.name,
      inputType: mappedInputType,
      options,
      filterOperators,
      dataType: 'text',
      attributeModel: attr.customAttributeType || 'standard',
    };
  });
});

const automationRuleEvents = computed(() =>
  AUTOMATION_RULE_EVENTS.map(event => ({
    value: event.key,
    label: t(`AUTOMATION.EVENTS.${event.value}`),
  }))
);

const onEventSelect = value => {
  automation.value.event_name = value;
  props.onEventChange();
};

const dirty = computed(
  () => isOpen.value && JSON.stringify(automation.value) !== snapshot.value
);
const scope = computed(
  () => `${t('SIDEBAR.SETTINGS')} · ${t('AUTOMATION.HEADER')}`
);

const hasAutomationMutated = computed(() => {
  return Boolean(
    automation.value?.conditions[0]?.values ||
      automation.value?.actions[0]?.action_params?.length
  );
});

const automationActionTypes = computed(() => {
  const actionTypes = isCloudFeatureEnabled('sla')
    ? AUTOMATION_ACTION_TYPES
    : AUTOMATION_ACTION_TYPES.filter(({ key }) => key !== 'add_sla');

  return actionTypes.map(action => ({
    ...action,
    label: t(`AUTOMATION.ACTIONS.${action.label}`),
  }));
});

const hasConditionErrors = computed(() =>
  Object.keys(errors.value).some(key => key.startsWith('condition_'))
);

const hasActionErrors = computed(() =>
  Object.keys(errors.value).some(key => key.startsWith('action_'))
);

watch(
  () => automation.value,
  () => {
    if (Object.keys(errors.value).length) {
      errors.value = {};
    }
  },
  { deep: true }
);

const isConditionsValid = () => {
  if (!conditionsRef.value) return true;
  return conditionsRef.value.every(condition => condition.validate());
};

const resetValidation = () => {
  errors.value = {};
  conditionsRef.value?.forEach(c => c.resetValidation());
};

const syncCustomAttributeTypes = () => {
  automation.value.conditions.forEach(condition => {
    const filterType = filterTypes.value.find(
      ft => ft.attributeKey === condition.attribute_key
    );
    condition.custom_attribute_type =
      filterType?.attributeModel === 'standard'
        ? ''
        : filterType?.attributeModel || '';
  });
};

const open = () => {
  resetValidation();
  isOpen.value = true;
  // the edit wrapper fills the rule in a watcher, after this call
  nextTick(() => {
    snapshot.value = JSON.stringify(automation.value);
  });
};

const close = () => {
  resetValidation();
  isOpen.value = false;
};

// Another drawer may only open once this one lets go (asks first when it has changes).
const runGuarded = action =>
  sheetRef.value ? sheetRef.value.runGuarded(action) : action();

const emitSaveAutomation = () => {
  syncCustomAttributeTypes();
  const conditionsValid = isConditionsValid();
  errors.value = validateAutomation(automation.value);
  if (Object.keys(errors.value).length === 0 && conditionsValid) {
    const payload = generateAutomationPayload(automation.value);
    emit('save', payload, props.mode);
  }
};

defineExpose({ open, close, runGuarded });
</script>

<template>
  <EditSheet
    ref="sheetRef"
    v-model:open="isOpen"
    :title="$t(titleKey)"
    :scope="scope"
    :dirty="dirty"
    :save-label="$t(submitKey)"
    @save="emitSaveAutomation"
  >
    <template v-if="automation">
      <FoldingGroup :title="$t('SETTINGS_PIECES.SECTIONS.DETAILS')">
        <div class="grid gap-4">
          <woot-input
            v-model="automation.name"
            :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
            type="text"
            :class="{ error: errors.name }"
            :error="errors.name ? $t('AUTOMATION.ADD.FORM.NAME.ERROR') : ''"
            :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
          />
          <woot-input
            v-model="automation.description"
            :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
            type="text"
            :class="{ error: errors.description }"
            :error="
              errors.description ? $t('AUTOMATION.ADD.FORM.DESC.ERROR') : ''
            "
            :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
          />
          <Field :data-invalid="!!errors.event_name || undefined">
            <FieldLabel for="automation-event">
              {{ $t('AUTOMATION.ADD.FORM.EVENT.LABEL') }}
            </FieldLabel>
            <OptionSelect
              id="automation-event"
              :model-value="automation.event_name"
              :options="automationRuleEvents"
              @update:model-value="onEventSelect"
            />
            <FieldError v-if="errors.event_name">
              {{ $t('AUTOMATION.ADD.FORM.EVENT.ERROR') }}
            </FieldError>
            <p
              v-if="!isEditMode && hasAutomationMutated"
              class="type-polaris-caption text-polaris-muted-foreground"
            >
              {{ $t('AUTOMATION.FORM.RESET_MESSAGE') }}
            </p>
          </Field>
        </div>
      </FoldingGroup>
      <FoldingGroup :title="$t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL')">
        <ul
          class="m-0 grid list-none gap-4 rounded-polaris-lg border border-solid p-3"
          :class="
            hasConditionErrors
              ? 'border-polaris-destructive'
              : 'border-polaris-border'
          "
        >
          <template v-for="(condition, i) in automation.conditions" :key="i">
            <ConditionRow
              v-if="i === 0"
              ref="conditionsRef"
              v-model:attribute-key="automation.conditions[i].attribute_key"
              v-model:filter-operator="automation.conditions[i].filter_operator"
              v-model:values="automation.conditions[i].values"
              :filter-types="filterTypes"
              :show-query-operator="false"
              @remove="removeFilter(i)"
            />
            <ConditionRow
              v-else
              ref="conditionsRef"
              v-model:attribute-key="automation.conditions[i].attribute_key"
              v-model:filter-operator="automation.conditions[i].filter_operator"
              v-model:query-operator="
                automation.conditions[i - 1].query_operator
              "
              v-model:values="automation.conditions[i].values"
              :filter-types="filterTypes"
              show-query-operator
              @remove="removeFilter(i)"
            />
          </template>
          <li>
            <NextButton
              icon="i-lucide-plus"
              slate
              outline
              sm
              :label="$t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL')"
              @click="appendNewCondition"
            />
          </li>
        </ul>
      </FoldingGroup>
      <FoldingGroup :title="$t('AUTOMATION.ADD.FORM.ACTIONS.LABEL')">
        <ul
          class="m-0 grid list-none rounded-polaris-lg border border-solid p-3"
          :class="
            hasActionErrors
              ? 'border-polaris-destructive'
              : 'border-polaris-border'
          "
        >
          <AutomationActionInput
            v-for="(action, i) in automation.actions"
            :key="i"
            v-model="automation.actions[i]"
            :action-types="automationActionTypes"
            dropdown-max-height="max-h-[7.5rem]"
            :dropdown-values="getActionDropdownValues(action.action_name)"
            :show-action-input="
              showActionInput(automationActionTypes, action.action_name)
            "
            :error-message="
              errors[`action_${i}`]
                ? $t(`AUTOMATION.ERRORS.${errors[`action_${i}`]}`)
                : ''
            "
            :initial-file-name="
              isEditMode ? getFileName(action, automation.files) : ''
            "
            @reset-action="resetAction(i)"
            @remove-action="removeAction(i)"
          />
          <li class="pt-2">
            <NextButton
              icon="i-lucide-plus"
              slate
              outline
              sm
              :label="$t('AUTOMATION.ADD.ACTION_BUTTON_LABEL')"
              @click="appendNewAction"
            />
          </li>
        </ul>
      </FoldingGroup>
    </template>
  </EditSheet>
</template>
